import { useEffect, useMemo } from "react"
import { useInfiniteQuery, useMutation, useQuery } from "react-query"

import { queryProducts } from "src/api/Billing/Items"
import {
  queryRefundablePaymentMethods,
  querySaleRefundablePaymentMethods,
} from "src/api/Billing/Payments"
import { reversePayment } from "src/api/Payments"

import { getCurrentMarinaSlug } from "src/utils/url/parsing/marina"

export const useGetRefundablePaymentMethods = ({
  reservationId,
  saleId,
  isSettling = false,
  options,
}) => {
  const marinaSlug = getCurrentMarinaSlug()

  const queryKey = [
    "refundablePaymentMethods",
    { saleId, reservationId, isSettling },
    marinaSlug,
  ]

  const queryFn = reservationId
    ? () =>
        queryRefundablePaymentMethods({ reservationId, marinaSlug, isSettling })
    : () => querySaleRefundablePaymentMethods({ saleId, marinaSlug })

  const { data, isFetching, isError } = useQuery({
    queryKey,
    queryFn,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    ...options,
  })

  return { data, isFetching, isError }
}

export const useReversePayment = ({
  reservationId,
  saleId,
  paymentId,
  options,
}) => {
  const marinaSlug = getCurrentMarinaSlug()

  const queryKey = ["reversePayment", { saleId, reservationId }, marinaSlug]

  const { mutate, isLoading } = useMutation({
    queryKey,
    mutationFn: () =>
      reversePayment({
        marinaSlug,
        invoiceId: paymentId,
      }),
    ...options,
  })
  return { mutate, isLoading }
}

export const useQueryProducts = ({
  marinaSlug,
  queryKey,
  reservationId = null,
  enabled = true,
}) => {
  const RESERVATION_EXISTS_PRODUCT_CATEGORIES = [
    "metered_electric",
    "long_term_storage",
  ]

  const excludedProductCategories = reservationId
    ? []
    : RESERVATION_EXISTS_PRODUCT_CATEGORIES

  const {
    isFetching: isFetchingProducts,
    isLoading: isLoadingProducts,
    isError: isErrorProducts,
    data: productsData,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam = 1 }) =>
      queryProducts({
        page: pageParam,
        marinaSlug,
        excludedCategories: excludedProductCategories,
        availability: ["contract_reservation_add_ons"],
      }),
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.length) {
        return pages.length + 1
      }
    },
    refetchOnWindowFocus: false,
    enabled,
  })

  const products = useMemo(
    () => (productsData?.pages || []).flatMap((page) => page),
    [productsData]
  )

  useEffect(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  return {
    isFetchingProducts,
    isLoadingProducts,
    isErrorProducts,
    hasNextPage,
    products,
  }
}
